import { Client } from '@botpress/client'
import { auth, Credentials } from '../auth/index.js'
import { Dependencies, ValidationError, ValidationErrorCode, ValidationSeverity } from '../agent-project/types.js'
import { IntegrationDefinition, ParsedIntegration, IntegrationValidationResult, IntegrationRef } from './types.js'
import { EnhancedIntegrationCache } from './enhanced-cache.js'
import { IntegrationParser } from '../agent-project/dependencies-json.js'
import { ValidationErrors } from '../agent-project/validation-errors.js'

export interface IntegrationManagerOptions {
  noCache?: boolean
  workspaceId?: string
  credentials?: Credentials
}

export class IntegrationManager {
  private cache: EnhancedIntegrationCache
  private options: IntegrationManagerOptions
  private client?: Client

  constructor(options: IntegrationManagerOptions = {}) {
    this.options = options
    this.cache = new EnhancedIntegrationCache(options.noCache || false)
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = this.options.credentials || (await auth.getActiveCredentials())

      // Ensure we have all required credentials
      if (!this.options.workspaceId && !credentials.workspaceId) {
        throw new Error('No workspace ID found in current profile. Please login again with "adk login"')
      }

      const workspaceId = this.options.workspaceId || credentials.workspaceId

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  /**
   * Load and validate all integrations from dependencies
   */
  async loadIntegrations(dependencies: Dependencies): Promise<{
    integrations: ParsedIntegration[]
    errors: ValidationError[]
    warnings: ValidationError[]
  }> {
    const errors: ValidationError[] = []
    const warnings: ValidationError[] = []

    // Parse integrations from dependencies
    const parseResult = IntegrationParser.parseIntegrations(dependencies)
    const integrations = parseResult.integrations
    errors.push(...parseResult.errors)

    // Check for duplicates
    const duplicateWarnings = IntegrationParser.checkDuplicates(integrations)
    warnings.push(...duplicateWarnings)

    // Fetch integration definitions in parallel
    const fetchPromises = integrations.map(async (integration) => {
      try {
        const definition = await this.fetchIntegration(integration.ref)
        integration.definition = definition

        // Validate the integration
        const validation = this.validateIntegration(integration)
        integration.validationResult = validation

        if (!validation.valid) {
          validation.errors.forEach(() => {
            errors.push(ValidationErrors.unknownIntegration(integration.alias, integration.ref.fullName))
          })
        }

        if (validation.warnings.length > 0) {
          validation.warnings.forEach((warn) => {
            warnings.push(ValidationErrors.warning(warn, 'dependencies.json'))
          })
        }
      } catch (error) {
        // Use the specific error message if available
        if (error instanceof Error && error.message.includes('version')) {
          // Version-specific error
          errors.push(ValidationErrors.integrationVersionError(integration.alias, error.message))
        } else {
          // Integration not found - use detailed error message if available
          const errorMessage = error instanceof Error ? error.message : undefined
          errors.push(ValidationErrors.unknownIntegration(integration.alias, integration.ref.fullName, errorMessage))
        }
      }
    })

    await Promise.all(fetchPromises)

    // Check if any integration has channels
    const hasChannels = integrations.some(
      (i) => i.definition?.channels && Object.keys(i.definition.channels).length > 0
    )

    if (!hasChannels && integrations.length > 0) {
      warnings.push(
        ValidationErrors.warning(
          'No integrations with channels found. Your agent may not be able to receive messages.',
          'dependencies.json',
          'Add an integration with channels (e.g., slack, webchat) to enable conversations'
        )
      )
    }

    return { integrations, errors, warnings }
  }

  /**
   * Fetch integration definition from API or cache
   */
  public async fetchIntegration(ref: IntegrationRef): Promise<IntegrationDefinition> {
    // Level 1: Check version resolution cache (name@version → intver + updatedAt)
    const cachedResolution = await this.cache.getResolution(ref.name, ref.version, ref.workspace)

    if (cachedResolution) {
      // Level 2: Check definition cache (intver + updatedAt → definition)
      const cachedDefinition = await this.cache.getDefinition(
        cachedResolution.integrationId,
        cachedResolution.updatedAt
      )

      if (cachedDefinition) {
        return cachedDefinition
      }
    }

    // No cache hit, fetch from API
    const client = await this.getClient()

    // Try to find the integration (private first, then public)
    const integration = await this._findPrivateOrPublicIntegration(client, ref)
    if (!integration) {
      throw await this._buildIntegrationNotFoundError(client, ref)
    }

    // Cache Level 1: Version resolution (name@version → intver + updatedAt)
    await this.cache.setResolution(ref.name, ref.version, ref.workspace, integration.id, integration.updatedAt)

    // Cache Level 2: Definition (intver + updatedAt → definition)
    const definition: IntegrationDefinition = integration
    await this.cache.setDefinition(integration.id, integration.updatedAt, definition)

    return definition
  }

  /**
   * Find integration in private workspace first, then public hub
   * Similar to ApiClient.findPublicOrPrivateIntegration from @botpress/cli
   */
  private async _findPrivateOrPublicIntegration(
    client: Client,
    ref: IntegrationRef
  ): Promise<IntegrationDefinition | undefined> {
    // Try private/workspace integration first
    const privateIntegration = await this._findPrivateIntegration(client, ref)
    if (privateIntegration) {
      return privateIntegration
    }

    // Try public integration
    const publicIntegration = await this._findPublicIntegration(client, ref)
    if (publicIntegration) {
      return publicIntegration
    }

    return undefined
  }

  /**
   * Find private/workspace integration
   */
  private async _findPrivateIntegration(
    client: Client,
    ref: IntegrationRef
  ): Promise<IntegrationDefinition | undefined> {
    try {
      // For workspace integrations, always use "latest" as they don't support version pinning
      const version = ref.workspace && ref.version !== 'latest' ? 'latest' : ref.version

      const response = await client.getIntegrationByName({
        name: ref.fullName,
        version,
      })
      return response.integration
    } catch (error) {
      // Return undefined on ResourceNotFound, throw on other errors
      if (this._isResourceNotFoundError(error)) {
        return undefined
      }
      throw error
    }
  }

  /**
   * Find public integration
   */
  private async _findPublicIntegration(
    client: Client,
    ref: IntegrationRef
  ): Promise<IntegrationDefinition | undefined> {
    try {
      const response = await client.getPublicIntegration({
        name: ref.fullName,
        version: ref.version,
      })
      return response.integration
    } catch (error) {
      // Return undefined on ResourceNotFound, throw on other errors
      if (this._isResourceNotFoundError(error)) {
        return undefined
      }
      throw error
    }
  }

  /**
   * Check if error is a ResourceNotFound error
   */
  private _isResourceNotFoundError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'type' in error) {
      return (error as any).type === 'ResourceNotFound'
    }
    return false
  }

  /**
   * Build a helpful error message when integration is not found
   */
  private async _buildIntegrationNotFoundError(client: Client, ref: IntegrationRef): Promise<Error> {
    // If no workspace prefix, simple error
    if (!ref.workspace) {
      return new Error(`Integration "${ref.name}" not found in the official Botpress hub`)
    }

    // Get current workspace info to compare handles
    let currentWorkspaceHandle: string | undefined
    try {
      const credentials = this.options.credentials || (await auth.getActiveCredentials())
      const workspaceId = this.options.workspaceId || credentials.workspaceId

      if (workspaceId) {
        // List workspaces to find current workspace and get its handle
        const currentWorkspace = await client.getWorkspace({ id: workspaceId })
        currentWorkspaceHandle = currentWorkspace?.handle
      }
    } catch {
      // If we can't get workspace info, use generic error
      return new Error(`Integration "${ref.name}" not found in workspace "${ref.workspace}"`)
    }

    // Case 1: Trying to install from the same workspace
    if (currentWorkspaceHandle === ref.workspace) {
      return new Error(
        `Integration "${ref.name}" not found in workspace "${ref.workspace}". ` +
          `Are you sure you published the integration? ` +
          `Run 'adk deploy' to publish it to your workspace.`
      )
    }

    // Case 2: Trying to install from a different workspace (likely private)
    return new Error(
      `Integration "${ref.name}" not found in workspace "${ref.workspace}". ` +
        `This integration may be private. Private integrations can only be installed in the same workspace. ` +
        `If you want to share this integration with other workspaces, deploy it with --visibility="unlisted" or --visibility="public".`
    )
  }

  /**
   * Validate an integration definition
   */
  private validateIntegration(integration: ParsedIntegration): IntegrationValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!integration.definition) {
      errors.push(`Integration definition not found for ${integration.alias}`)
      return { valid: false, errors, warnings }
    }

    // Check if integration has channels
    const hasChannels = integration.definition.channels && Object.keys(integration.definition.channels).length > 0

    // Check if integration requires configuration
    if (!integration.connection) {
      let requiresConfig = false

      // Check modern configurations (plural)
      if (integration.definition.configurations) {
        requiresConfig = Object.values(integration.definition.configurations).some(
          (config) => config.identifier?.required === true
        )
      }

      // Check legacy configuration (singular) - if it has required fields in schema
      if (!requiresConfig && integration.definition.configuration) {
        const config = integration.definition.configuration
        const schema = config.schema
        requiresConfig = schema?.required && schema.required.length > 0
      }

      if (requiresConfig) {
        warnings.push(
          `Integration '${integration.alias}' requires configuration. Add a connection in dependencies.json`
        )
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      missingChannels: !hasChannels,
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{ count: number; sizeBytes: number }> {
    const stats = await this.cache.getStats()
    // Combine resolution and definition cache stats
    return {
      count: stats.resolutions.count + stats.definitions.count,
      sizeBytes: stats.resolutions.sizeBytes + stats.definitions.sizeBytes,
    }
  }

  /**
   * Clear integration cache
   */
  async clearCache(): Promise<void> {
    await this.cache.clear()
  }
}
